import { z } from 'zod';
import { templateKeys, type TemplateKey } from './templates.ts';

export const MAX_DRAFTS_PER_USER = 30;
export const DRAFT_LIMIT_REACHED_MESSAGE = 'Maksimum oyun taslağı sayısına ulaştınız. En fazla 30 oyun taslağına sahip olabilirsiniz. Yeni oyun oluşturmadan önce, panelinizden kullanmadığınız taslakları silin.';

export const levelModes = ['manual'] as const;
export type LevelMode = (typeof levelModes)[number];
export const wagonStyles = ['wide', 'narrow'] as const;
export type WagonStyle = (typeof wagonStyles)[number];
export const bridgeModes = ['guided', 'challenge'] as const;
export type BridgeMode = (typeof bridgeModes)[number];
export const bridgeTimingModes = ['none', 'per-question'] as const;
export type BridgeTimingMode = (typeof bridgeTimingModes)[number];

export const mediaIdSchema = z.string().uuid('Görsel kimliği geçerli değil.').nullable().optional();

export type DraftMedia = {
  id: string;
  url: string;
  width: number;
  height: number;
};

export const choiceInputSchema = z.object({
  text: z.string().trim().max(100),
  isCorrect: z.boolean(),
  mediaId: mediaIdSchema,
});

export const questionInputSchema = z.object({
  prompt: z.string().trim().max(240),
  wagonStyle: z.enum(wagonStyles).optional(),
  levels: z
    .array(z.number().int().min(1).max(25))
    .length(1, 'Her soru yalnızca bir seviyeye ait olmalıdır.'),
  mediaId: mediaIdSchema,
  timeLimitSeconds: z.number().int().min(5).max(600).nullable().optional(),
  choices: z.array(choiceInputSchema).min(2, 'En az iki seçenek gereklidir.').max(6),
});

export const draftInputSchema = z
  .object({
    title: z.string().trim().min(3, 'Başlık en az 3 karakter olmalıdır.').max(100),
    templateKey: z.enum(templateKeys),
    levelMode: z.enum(levelModes),
    maxLevels: z.number().int().min(1).max(25),
    trackTime: z.boolean(),
    bridgeMode: z.enum(bridgeModes).optional(),
    timingMode: z.enum(bridgeTimingModes).optional(),
    /** Yalnız Denge Arenası: sorular bitince karışık sırayla öğrenci düşene kadar yeniden sorulur. */
    repeatUntilFall: z.boolean().optional(),
    questions: z.array(questionInputSchema).min(1).max(25),
  })
  .superRefine((draft, context) => {
    if (draft.repeatUntilFall && draft.templateKey !== 'balance-arena') {
      context.addIssue({ code: 'custom', path: ['repeatUntilFall'], message: 'Düşene kadar tekrar yalnız Denge Arenası için kullanılabilir.' });
    }
    if (draft.templateKey === 'train-adventure' && draft.maxLevels > 5) {
      context.addIssue({
        code: 'custom',
        path: ['maxLevels'],
        message: 'Tren Macerası en fazla 5 seviye olabilir.',
      });
    }
    if (draft.templateKey === 'fiery-route' && !draft.trackTime) {
      context.addIssue({
        code: 'custom',
        path: ['trackTime'],
        message: 'Alevli Rota toplam tamamlama süresini her zaman ölçer.',
      });
    }
    if (draft.templateKey === 'orbital-bridge') {
      if (!draft.trackTime) {
        context.addIssue({
          code: 'custom',
          path: ['trackTime'],
          message: 'Yörünge Köprüsü toplam ve soru sürelerini her zaman ölçer.',
        });
      }
      if (!draft.bridgeMode) {
        context.addIssue({
          code: 'custom',
          path: ['bridgeMode'],
          message: 'Yörünge Köprüsü için bir oyun modu seçilmelidir.',
        });
      }
      if (!draft.timingMode) {
        context.addIssue({
          code: 'custom',
          path: ['timingMode'],
          message: 'Yörünge Köprüsü için süre ayarı seçilmelidir.',
        });
      }
    }
    if (draft.templateKey === 'turbo-duel') {
      if (!draft.trackTime) {
        context.addIssue({
          code: 'custom',
          path: ['trackTime'],
          message: 'Turbo Düellosu toplam ve soru sürelerini her zaman ölçer.',
        });
      }
      if (!draft.timingMode) {
        context.addIssue({
          code: 'custom',
          path: ['timingMode'],
          message: 'Turbo Düellosu için süre ayarı seçilmelidir.',
        });
      }
    }
    if (draft.templateKey === 'balance-arena') {
      if (draft.maxLevels < 5 || draft.maxLevels > 25) {
        context.addIssue({ code: 'custom', path: ['maxLevels'], message: 'Denge Arenası 5–25 soru içermelidir.' });
      }
      if (!draft.trackTime) {
        context.addIssue({ code: 'custom', path: ['trackTime'], message: 'Denge Arenası dayanma ve soru sürelerini her zaman ölçer.' });
      }
      const questionImageCount = draft.questions.filter((question) => Boolean(question.mediaId)).length;
      const choiceImageCount = draft.questions.reduce(
        (total, question) => total + question.choices.filter((choice) => Boolean(choice.mediaId)).length,
        0,
      );
      if (questionImageCount > 10) {
        context.addIssue({ code: 'custom', path: ['questions'], message: 'Denge Arenası oyununda en fazla 10 soruya görsel ekleyebilirsiniz.' });
      }
      if (choiceImageCount > 10) {
        context.addIssue({ code: 'custom', path: ['questions'], message: 'Denge Arenası oyununda toplam en fazla 10 cevap seçeneğine görsel ekleyebilirsiniz.' });
      }
    }
    if (draft.templateKey === 'basket-learning') {
      if (draft.maxLevels < 4 || draft.maxLevels > 20) {
        context.addIssue({ code: 'custom', path: ['maxLevels'], message: 'Potada Öğren 4–20 soru içermelidir.' });
      }
      if (!draft.trackTime) {
        context.addIssue({ code: 'custom', path: ['trackTime'], message: 'Potada Öğren toplam ve soru sürelerini her zaman ölçer.' });
      }
      if (draft.timingMode !== 'none') {
        context.addIssue({ code: 'custom', path: ['timingMode'], message: 'Potada Öğren geri sayım kullanmaz.' });
      }
    }
    if (draft.templateKey === 'goal-arena') {
      if (draft.maxLevels < 4 || draft.maxLevels > 20) {
        context.addIssue({ code: 'custom', path: ['maxLevels'], message: 'Gol Arenası 4–20 soru içermelidir.' });
      }
      if (!draft.trackTime) {
        context.addIssue({ code: 'custom', path: ['trackTime'], message: 'Gol Arenası toplam ve soru sürelerini her zaman ölçer.' });
      }
      if (draft.timingMode !== 'none') {
        context.addIssue({ code: 'custom', path: ['timingMode'], message: 'Gol Arenası geri sayım kullanmaz.' });
      }
    }
    if (draft.templateKey === 'billionaire-quiz') {
      if (draft.maxLevels < 5 || draft.maxLevels > 20) {
        context.addIssue({ code: 'custom', path: ['maxLevels'], message: 'Kim Milyarder Olmak İster? 5–20 soru içermelidir.' });
      }
      if (!draft.trackTime) {
        context.addIssue({ code: 'custom', path: ['trackTime'], message: 'Kim Milyarder Olmak İster? toplam ve soru sürelerini rapor için ölçer.' });
      }
      if (draft.timingMode !== 'none') {
        context.addIssue({ code: 'custom', path: ['timingMode'], message: 'Kim Milyarder Olmak İster? geri sayım kullanmaz.' });
      }
    }
    if (draft.templateKey === 'ufo-adventure') {
      if (draft.maxLevels < 5 || draft.maxLevels > 20) context.addIssue({ code: 'custom', path: ['maxLevels'], message: 'UFO Macerası 5–20 soru içermelidir.' });
      if (!draft.trackTime || !draft.timingMode) context.addIssue({ code: 'custom', path: ['timingMode'], message: 'UFO Macerası geçiş modu ve süre ölçümü gerektirir.' });
      draft.questions.forEach((q, i) => {
        if (q.choices.length > 4) context.addIssue({ code: 'custom', path: ['questions', i, 'choices'], message: 'UFO Macerası 2–4 seçenek destekler.' });
        if (draft.timingMode === 'per-question' && (q.timeLimitSeconds == null || q.timeLimitSeconds < 10 || q.timeLimitSeconds > 120)) context.addIssue({ code: 'custom', path: ['questions', i, 'timeLimitSeconds'], message: 'Otomatik geçiş süresi 10–120 saniye olmalıdır.' });
        if (draft.timingMode === 'none' && q.timeLimitSeconds != null) context.addIssue({ code: 'custom', path: ['questions', i, 'timeLimitSeconds'], message: 'Seçim beklerken geçiş süresi kullanılmaz.' });
      });
    }
    if (draft.templateKey === 'station-run') {
      if (draft.maxLevels < 5 || draft.maxLevels > 20) context.addIssue({ code: 'custom', path: ['maxLevels'], message: 'İstasyon Koşusu 5–20 soru içermelidir.' });
      if (!draft.trackTime || draft.timingMode !== 'none') context.addIssue({ code: 'custom', path: ['timingMode'], message: 'İstasyon Koşusu süre ölçer ama soru süresi kullanmaz.' });
      draft.questions.forEach((q, i) => {
        if (q.choices.length > 4) context.addIssue({ code: 'custom', path: ['questions', i, 'choices'], message: 'İstasyon Koşusu 2–4 seçenek destekler.' });
        if (q.timeLimitSeconds != null) context.addIssue({ code: 'custom', path: ['questions', i, 'timeLimitSeconds'], message: 'İstasyon Koşusu soru süresi kullanmaz.' });
      });
    }
    if (draft.templateKey === 'lane-rally') {
      if (draft.maxLevels < 5 || draft.maxLevels > 20) context.addIssue({ code: 'custom', path: ['maxLevels'], message: 'Şerit Rallisi 5–20 soru içermelidir.' });
      if (!draft.trackTime || draft.timingMode !== 'none') context.addIssue({ code: 'custom', path: ['timingMode'], message: 'Şerit Rallisi süre ölçer ama geri sayım kullanmaz.' });
      draft.questions.forEach((q, i) => {
        if (q.choices.length > 4) context.addIssue({ code: 'custom', path: ['questions', i, 'choices'], message: 'Şerit Rallisi 2–4 seçenek destekler.' });
        if (q.timeLimitSeconds != null) context.addIssue({ code: 'custom', path: ['questions', i, 'timeLimitSeconds'], message: 'Şerit Rallisi soru süresi kullanmaz; öğrenci gazı bırakarak düşünür.' });
      });
    }
    draft.questions.forEach((question, questionIndex) => {
      if (!question.prompt && !question.mediaId) {
        context.addIssue({
          code: 'custom',
          path: ['questions', questionIndex, 'prompt'],
          message: 'Soruya metin veya görsel eklenmelidir.',
        });
      }

      question.choices.forEach((choice, choiceIndex) => {
        if (!choice.text && !choice.mediaId) {
          context.addIssue({
            code: 'custom',
            path: ['questions', questionIndex, 'choices', choiceIndex],
            message: 'Seçeneğe metin veya görsel eklenmelidir.',
          });
        }
      });

      const correctCount = question.choices.filter((choice) => choice.isCorrect).length;
      if (correctCount !== 1) {
        context.addIssue({
          code: 'custom',
          path: ['questions', questionIndex, 'choices'],
          message: 'Her soruda tam olarak bir doğru seçenek bulunmalıdır.',
        });
      }

      const normalizedChoices = question.choices
        .map((choice) => choice.text.toLocaleLowerCase('tr-TR'))
        .filter(Boolean);
      if (new Set(normalizedChoices).size !== normalizedChoices.length) {
        context.addIssue({
          code: 'custom',
          path: ['questions', questionIndex, 'choices'],
          message: 'Aynı seçeneği bir soruda birden fazla kullanamazsınız.',
        });
      }

      if (question.levels.some((level) => level > draft.maxLevels)) {
        context.addIssue({
          code: 'custom',
          path: ['questions', questionIndex, 'levels'],
          message: 'Soru, seçilen en yüksek seviyenin üzerinde kullanılamaz.',
        });
      }

      if (draft.templateKey === 'train-adventure' && question.mediaId && question.wagonStyle === 'narrow') {
        context.addIssue({
          code: 'custom',
          path: ['questions', questionIndex, 'wagonStyle'],
          message: 'Görsel içeren sorularda geniş vagon kullanılmalıdır.',
        });
      }

      if (draft.templateKey === 'orbital-bridge' || draft.templateKey === 'turbo-duel' || draft.templateKey === 'balance-arena' || draft.templateKey === 'basket-learning' || draft.templateKey === 'goal-arena') {
        if (question.choices.length > 5) {
          context.addIssue({
            code: 'custom',
            path: ['questions', questionIndex, 'choices'],
            message: `${draft.templateKey === 'turbo-duel' ? 'Turbo Düellosu' : draft.templateKey === 'balance-arena' ? 'Denge Arenası' : draft.templateKey === 'basket-learning' ? 'Potada Öğren' : draft.templateKey === 'goal-arena' ? 'Gol Arenası' : 'Yörünge Köprüsü'} sorularında en fazla 5 seçenek olabilir.`,
          });
        }
        if (draft.templateKey !== 'balance-arena' && draft.timingMode === 'per-question' && question.timeLimitSeconds == null) {
          context.addIssue({
            code: 'custom',
            path: ['questions', questionIndex, 'timeLimitSeconds'],
            message: 'Süreli oyunda her soru için 5–600 saniye arasında süre girilmelidir.',
          });
        }
      }

      if (draft.templateKey === 'billionaire-quiz' && question.choices.length !== 4) {
        context.addIssue({
          code: 'custom',
          path: ['questions', questionIndex, 'choices'],
          message: 'Kim Milyarder Olmak İster? sorularında tam dört seçenek bulunmalıdır.',
        });
      }

    });

    for (let level = 1; level <= draft.maxLevels; level += 1) {
      const questionCount = draft.questions.filter((question) => question.levels.includes(level)).length;
      const expectedQuestionCount = draft.templateKey === 'train-adventure' ? level : 1;
      if (questionCount !== expectedQuestionCount) {
        context.addIssue({
          code: 'custom',
          path: ['questions'],
          message: `${level}. seviyede tam olarak ${expectedQuestionCount} soru bulunmalıdır.`,
        });
      }
    }
  });

export type DraftInput = z.infer<typeof draftInputSchema>;
export type { TemplateKey };

export type DraftChoice = DraftInput['questions'][number]['choices'][number] & {
  id: string;
  media: DraftMedia | null;
};

export type DraftQuestion = Omit<DraftInput['questions'][number], 'choices'> & {
  id: string;
  media: DraftMedia | null;
  choices: DraftChoice[];
};

export type GameDraft = Omit<DraftInput, 'questions'> & {
  id: string;
  questions: DraftQuestion[];
  createdAt: number;
  updatedAt: number;
};

export type DraftSummary = Omit<GameDraft, 'questions'> & {
  questionCount: number;
};
